import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Heading, IconButton, Text, Image, Flex, Skeleton } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { RootState } from '../../../store/store';
import {
  getGroupsAction,
  getSubGroupsAction,
  setCarGroupsAction,
  setCarParentGroupAction,
} from '../../../modules/catalog/actions';
import { GroupDTO } from '../../../types';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { routes } from '../../../constants/routes';

export const Groups: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { type, mark, model, modification } = useParams();

  const { groupsList, subGroupsList, isLoading } = useSelector((state: RootState) => state.catalog);

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [nestedSubGroups, setNestedSubGroups] = useState<GroupDTO[]>([]);
  const [subGroupLoading, setSubGroupLoading] = useState(false);

  useEffect(() => {
    dispatch(getGroupsAction.request({ type, mark, model, modification }));
  }, [dispatch]);

  useEffect(() => {
    if (selectedGroupId && subGroupsList.length) {
      setNestedSubGroups(subGroupsList);
      setSubGroupLoading(false);
    } else {
      setNestedSubGroups(groupsList);
    }
  }, [groupsList, subGroupsList, selectedGroupId]);

  const handleGroupClick = (group: GroupDTO) => {
    if (selectedGroupId === group.id) return;

    setSelectedGroupId(group.id);
    dispatch(setCarGroupsAction(group.id));

    if (group.subGroups?.length > 0) {
      setNestedSubGroups(group.subGroups);
    } else if (group.needLoadSubGroups) {
      setSubGroupLoading(true);
      dispatch(getSubGroupsAction.request({ type, mark, model, modification, group: group.id }));
    } else {
      dispatch(setCarParentGroupAction(group.parentId));
      navigate(
        generatePath(routes.CATALOG_SCHEME, {
          type,
          mark,
          model,
          modification,
          group: group.id,
          parentId: group.parentId,
        }),
      );
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Flex height="100vh" flexDir="column">
      <Flex alignItems="center" onClick={handleBack} cursor="pointer">
        <IconButton aria-label="Back" icon={<ChevronLeftIcon boxSize="32px" />} variant="ghost" />
        <Heading size="lg">Назад</Heading>
      </Flex>
      <Flex flex="1" overflow="hidden">
        {/* Меню */}
        <Box
          as="nav"
          width="30%"
          borderRight="1px solid #E2E8F0"
          overflowY="auto"
          maxH="100%"
          p={4}
        >
          {isLoading.groups ? (
            <Skeleton height="20px" my="4" />
          ) : (
            groupsList.map((group: GroupDTO) => (
              <Box key={group.id} mt={4}>
                <Box
                  display="flex"
                  alignItems="center"
                  onClick={() => handleGroupClick(group)}
                  style={{ cursor: 'pointer' }}
                >
                  <IconButton
                    aria-label="Expand Group"
                    icon={selectedGroupId === group.id ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    size="sm"
                    variant="ghost"
                    mr={2}
                  />
                  <Heading size="md" mb={2}>
                    {group.name}
                  </Heading>
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* Тело */}
        <Box flex="1" overflowY="auto" p={4}>
          {isLoading.groups || subGroupLoading ? (
            <>
              <Skeleton height="200px" my="4" />
              <Skeleton height="200px" my="4" />
              <Skeleton height="200px" my="4" />
            </>
          ) : nestedSubGroups.length > 0 ? (
            nestedSubGroups.map((subGroup: GroupDTO) => (
              <Box
                key={subGroup.id}
                p={4}
                mb={4}
                borderWidth="1px"
                borderRadius="lg"
                display="flex"
                flexDirection={{ base: 'column', md: 'row' }}
                onClick={() => handleGroupClick(subGroup)}
              >
                <Box
                  flex="1"
                  p={2}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  bg={subGroup.image ? 'transparent' : 'gray.200'}
                  borderRadius="md"
                  minHeight="150px"
                >
                  {subGroup.image ? (
                    <Image
                      src={subGroup.image}
                      alt={subGroup.name}
                      maxWidth="100%"
                      borderRadius="md"
                    />
                  ) : (
                    <Box
                      width="100%"
                      height="100%"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      bg="gray.200"
                      borderRadius="md"
                      fontSize="sm"
                      color="gray.500"
                    >
                      {subGroup.name}
                    </Box>
                  )}
                </Box>
                <Box flex="2" p={2}>
                  <Heading size="md">{subGroup.name}</Heading>
                  <Text fontSize="sm" color="gray.600" mt={2}>
                    Detail found in node {subGroup.id}
                  </Text>
                </Box>
              </Box>
            ))
          ) : (
            <Text fontSize="lg" color="gray.500" mt={4}>
              No available subgroups or details.
            </Text>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};
